import { useCallback, useState } from "react";
import { Form } from "antd";
import { isNil, trim } from "lodash";
import { AiccLessonEntry, AiccLessonEntryApi, AiccLessonEntryPayload } from "types/model/aiccLearningAssets";
import { RunWrite } from "./useLearningAssetVersions";
import { skipLearningAssetConfirm } from "../confirmations";
import { toLessonEntryPayload } from "../LessonEntries/toLessonEntryPayload";

export function useLessonEntryEdit(api: AiccLessonEntryApi, runWrite: RunWrite<AiccLessonEntry>) {
  const [editForm] = Form.useForm<AiccLessonEntryPayload>();
  const [editingEntry, setEditingEntry] = useState<AiccLessonEntry>();

  const cancelEdit = useCallback(() => {
    setEditingEntry(undefined);
    editForm.resetFields();
  }, [editForm]);

  const startEdit = useCallback(
    (entry: AiccLessonEntry) => {
      setEditingEntry(entry);
      editForm.setFieldsValue({
        level: entry.level,
        lesson: entry.lesson,
        text: entry.text,
        translation: entry.translation,
        transliteration: entry.transliteration,
      });
    },
    [editForm]
  );

  const saveEdit = useCallback(() => {
    if (isNil(editingEntry)) {
      return;
    }
    editForm
      .validateFields()
      .then((values) =>
        runWrite(
          (languageId) => api.update(languageId, editingEntry, toLessonEntryPayload(values)),
          `Updated ${trim(values.text)} successfully.`,
          cancelEdit,
          { confirm: skipLearningAssetConfirm }
        )
      )
      .catch(() => undefined);
  }, [api, cancelEdit, editForm, editingEntry, runWrite]);

  const handleValuesChange = useCallback(
    (changed: Partial<AiccLessonEntryPayload>) => {
      if (changed.level !== undefined) {
        editForm.setFieldsValue({ lesson: undefined });
      }
    },
    [editForm]
  );

  return { editForm, editingId: editingEntry?.id, startEdit, cancelEdit, saveEdit, handleValuesChange };
}
